import { HttpClient, HttpParams } from "@angular/common/http";
import { getFileNameExtension, isNullOrUndefined } from "@pineapplelab/util";
import { Injectable } from "@angular/core";
import { Contract, ContractSigner } from "@app/models/firebase/contract";
import { ContractStatusEnum } from "@app/enums/contract-status.enum";
import { Project } from "@app/models/firebase/project";
import { CompleteSignerPayload, SendSignedPdfPayload, SendSigningEmailsPayload } from "@app/models/dto/contract.dto";
import { API_URLS } from '@app/utils/urls';
import { ApiService } from '../api/api.service';
import { DbService } from "../db/db.service";
import { StorageService } from "../storage/storage.service";
import { ToasterService } from "../toaster/toaster.service";

export { CompleteSignerPayload, SendSignedPdfPayload, SendSigningEmailsPayload };

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  constructor(
    private db: DbService,
    private storageService: StorageService,
    private http: HttpClient,
    private toasterService: ToasterService,
    private apiService: ApiService,
  ) { }

  private assertRequiredParams(params: Record<string, string | undefined | null>): void {
    for (const [name, value] of Object.entries(params)) {
      if (!value || !value.trim()) {
        const msg = `ContractService: required parameter "${name}" cannot be empty.`;
        this.toasterService.showError(msg);
        throw new Error(msg);
      }
    }
  }

  async save(model: Contract, projectId: string) {
    this.assertRequiredParams({ projectId });
    if (!model) {
      const msg = 'ContractService: contract model cannot be null.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }

    if (isNullOrUndefined(model.id)) {
      model.id = this.db.createId();
    }

    await this.saveOrRemovePDFIfNecessary(model, projectId);
    const json = model.toJson();

    await this.db.firestore.collection(Project.PATH).doc(projectId).collection(Contract.PATH)
      .doc(model.id).set(json, { merge: true });

    return json;
  }

  async getContract(projectId: string, id: string) {
    this.assertRequiredParams({ projectId, id });
    const snapshot = await this.db.firestore.collection(Project.PATH).doc(projectId).collection(Contract.PATH)
      .doc(id).get();
    if (!snapshot.exists) {
      return null;
    }
    return new Contract(snapshot.data());
  }

  async getAll(projectId: string) {
    this.assertRequiredParams({ projectId });
    const snapshot = await this.db.firestore.collection(Project.PATH).doc(projectId).collection(Contract.PATH)
      .where('is_deleted', '==', false)
      .get();

    return snapshot.docs.map((doc: any) => new Contract(doc.data()));
  }

  async getContracts(projectId: string): Promise<Contract[]> {
    this.assertRequiredParams({ projectId });
    const snapshot = await this.db.firestore
      .collection(Project.PATH).doc(projectId)
      .collection(Contract.PATH)
      .where('is_deleted', '==', false)
      .get();
    return snapshot.docs.map(doc => new Contract(doc.data()));
  }

  async getContractsReutilizable(projectId: string) {
    this.assertRequiredParams({ projectId });
    const snapshot = await this.db.firestore.collection(Project.PATH).doc(projectId).collection(Contract.PATH)
      .where('is_reutilizable', '==', true)
      .get();
    return snapshot.docs.map(doc => new Contract(doc.data()));
  }

  async getSigningContract(projectId: string, contractId: string, token: string) {
    this.assertRequiredParams({ projectId, contractId, token });
    const response = await this.apiService.get(`/contracts/signing-contract/${projectId}/${contractId}/${token}`, new HttpParams()) as any;
    return response || null;
  }

  async saveOrRemovePDFIfNecessary(model: Contract, projectId: string) {
    if (!model || !projectId) {
      return;
    }

    const commonPath = `${Project.PATH}/${projectId}/${Contract.PATH}/{${model.id}}`;
    if (!isNullOrUndefined(model.__pdf)) {
      const path = `${commonPath}/contract.${getFileNameExtension(model.__pdf.name)}`;
      model.pdf = await this.storageService.uploadFile(path, model.__pdf, { contentType: 'application/pdf' });
    } else if (model.pdf === null) {
      model.pdf = null;
    }
  }

  remove(projectId: string, modelId: string) {
    this.assertRequiredParams({ projectId, modelId });
    return this.db.firestore.collection(Project.PATH).doc(projectId).collection(Contract.PATH).doc(modelId).delete();
  }

  sign(projectId: string, contractId: string, signatureData?: string) {
    this.assertRequiredParams({ projectId, contractId });
    return this.http.post(`${this.apiService.apiUrl}${API_URLS.CONTRACTS.SIGN}`, { projectId, contractId, signatureData }).toPromise();
  }

  private signersRef(projectId: string, contractId: string) {
    return this.db.firestore
      .collection(Project.PATH).doc(projectId)
      .collection(Contract.PATH).doc(contractId)
      .collection('signers');
  }

  async saveSigner(projectId: string, contractId: string, signer: ContractSigner): Promise<void> {
    this.assertRequiredParams({ projectId, contractId });
    if (!signer?.id) {
      const msg = 'ContractService: signer must have a valid id.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }

    const data: { [key: string]: any } = {
      id: signer.id,
      role: signer.role ?? null,
      name: signer.name ?? null,
      email: signer.email ?? null,
      status: signer.status,
      signerToken: signer.signerToken || null,
      signedAt: signer.signedAt || null,
      signature: signer.signature || null,
      ip: signer.ip || null,
      userAgent: signer.userAgent || null,
      documentHash: signer.documentHash || null,
    };
    await this.signersRef(projectId, contractId).doc(signer.id).set(data, { merge: true });
  }

  async getSigners(projectId: string, contractId: string): Promise<ContractSigner[]> {
    this.assertRequiredParams({ projectId, contractId });
    const snapshot = await this.signersRef(projectId, contractId).get();
    return snapshot.docs.map(doc => doc.data() as ContractSigner);
  }

  async saveSignerFieldValues(projectId: string, contractId: string, signerId: string, fieldValues: { [key: string]: string }): Promise<void> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    if (!fieldValues || typeof fieldValues !== 'object') {
      const msg = 'ContractService: fieldValues must be a valid object.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }
    await this.signersRef(projectId, contractId).doc(signerId).set({ fieldValues }, { merge: true });
  }

  async saveSignerFiles(projectId: string, contractId: string, signerId: string, files: string[]): Promise<void> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    if (!Array.isArray(files)) {
      const msg = 'ContractService: files must be an array.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }
    await this.signersRef(projectId, contractId).doc(signerId).set({ files }, { merge: true });
  }

  async completeSignerForm(
    projectId: string,
    contractId: string,
    signerId: string,
    fieldValues: { [key: string]: string },
    metadata?: { ip?: string; userAgent?: string; documentHash?: string }
  ): Promise<void> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    await this.signersRef(projectId, contractId).doc(signerId).set(
      { fieldValues, status: ContractStatusEnum.SIGNED, signedAt: new Date(), ...(metadata || {}) },
      { merge: true }
    );
  }

  async setSignerStatus(projectId: string, contractId: string, signerId: string, status: ContractStatusEnum): Promise<void> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    if (!status) {
      const msg = 'ContractService: status cannot be empty.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }
    await this.signersRef(projectId, contractId).doc(signerId).set({ status }, { merge: true });
  }

  async getSignerById(projectId: string, contractId: string, signerId: string): Promise<ContractSigner | null> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    const snapshot = await this.signersRef(projectId, contractId).doc(signerId).get();
    if (!snapshot.exists) {
      return null;
    }
    return snapshot.data() as ContractSigner;
  }

  async getSignerByToken(projectId: string, contractId: string, token: string): Promise<ContractSigner | null> {
    this.assertRequiredParams({ projectId, contractId, token });
    const snapshot = await this.signersRef(projectId, contractId)
      .where('signerToken', '==', token)
      .get();
    if (snapshot.empty) {
      return null;
    }
    return snapshot.docs[0].data() as ContractSigner;
  }

  async completeSigner(
    projectId: string,
    contractId: string,
    signerId: string,
    signatureData: string,
    metadata?: { ip?: string; userAgent?: string; documentHash?: string }
  ): Promise<void> {
    this.assertRequiredParams({ projectId, contractId, signerId });
    if (!signatureData) {
      const msg = 'ContractService: signatureData cannot be empty.';
      this.toasterService.showError(msg);
      throw new Error(msg);
    }

    const now = new Date();

    await this.signersRef(projectId, contractId).doc(signerId).set({
      status: ContractStatusEnum.SIGNED,
      signedAt: now,
      signature: signatureData,
      ...(metadata || {}),
    }, { merge: true });

    const allSigners = await this.getSigners(projectId, contractId);
    const allSigned = allSigners.every(s => s.status === ContractStatusEnum.SIGNED || s.id === signerId);
    const contractStatus = allSigned ? ContractStatusEnum.SIGNED : ContractStatusEnum.PARTIALLY_SIGNED;

    await this.db.firestore
      .collection(Project.PATH).doc(projectId)
      .collection(Contract.PATH).doc(contractId)
      .set({ status: contractStatus, ...(allSigned ? { signedAt: now } : {}) }, { merge: true });
  }

  async getContractsForSigner(projectId: string, userId: string): Promise<Contract[]> {
    this.assertRequiredParams({ projectId, userId });
    const contractsSnapshot = await this.db.firestore
      .collection(Project.PATH).doc(projectId)
      .collection(Contract.PATH)
      .where('is_deleted', '==', false)
      .get();

    const results: Contract[] = [];
    await Promise.all(
      contractsSnapshot.docs.map(async (contractDoc) => {
        const signerDoc = await this.signersRef(projectId, contractDoc.id).doc(userId).get();
        if (signerDoc.exists) {
          results.push(new Contract(contractDoc.data()));
        }
      })
    );
    return results;
  }

  sendSigningEmails(payload: SendSigningEmailsPayload): Promise<{ success: boolean; sent: number; errors: string[] }> {
    if (!payload?.projectId || !payload?.contractId) {
      const msg = 'ContractService: sendSigningEmails requires projectId and contractId.';
      this.toasterService.showError(msg);
      return Promise.reject(new Error(msg));
    }
    if (!Array.isArray(payload.signers) || payload.signers.length === 0) {
      const msg = 'ContractService: sendSigningEmails requires at least one signer.';
      this.toasterService.showError(msg);
      return Promise.reject(new Error(msg));
    }
    return this.http.post<any>(`${this.apiService.apiUrl}${API_URLS.CONTRACTS.SEND_SIGNING_EMAILS}`, payload).toPromise();
  }

  sendSignedPdf(payload: SendSignedPdfPayload): Promise<{ success: boolean; sent: number; errors: string[] }> {
    if (!payload?.projectId || !payload?.contractId || !payload?.signerId || !payload?.signerEmail) {
      const msg = 'ContractService: sendSignedPdf requires projectId, contractId, signerId, and signerEmail.';
      this.toasterService.showError(msg);
      return Promise.reject(new Error(msg));
    }
    return this.http.post<any>(`${this.apiService.apiUrl}${API_URLS.CONTRACTS.SEND_SIGNED_PDF}`, payload).toPromise();
  }

  completeSignerWithMetadata(payload: CompleteSignerPayload): Promise<{ success: boolean; contractStatus: string }> {
    if (!payload?.projectId || !payload?.contractId || !payload?.signerId || !payload?.signerEmail) {
      const msg = 'ContractService: completeSignerWithMetadata requires projectId, contractId, signerId, and signerEmail.';
      this.toasterService.showError(msg);
      return Promise.reject(new Error(msg));
    }
    return this.http.post<any>(`${this.apiService.apiUrl}${API_URLS.CONTRACTS.COMPLETE_SIGNER}`, payload).toPromise();
  }
}
